// ============================================================================
// GST SOURCE SETUP — G-1 slice 4a (ADR-0025 Revision 1, R1.4)
// ============================================================================
// Done once per client-year, before monthly work: upload the client's sample
// files together, see what each provides and what is missing, where files
// overlap and whether they agree, then decide which files to rely on and which
// file each part of the return is built from. Finalising writes the expected
// files — with the sheets and mapping profiles learned from the samples — so
// the first real month parses with no dialog.
//
// Samples never feed a return. They are parsed through the ordinary dialog
// (in sample mode) and mapped in the ordinary preview.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { gstEngagementService } from 'api/services/gstEngagementService';
import { gstSourceDocumentService } from 'api/services/gstSourceDocumentService';
import { gstSourceSetupService } from 'api/services/gstSourceSetupService';
import { gstSubmissionService } from 'api/services/gstSubmissionService';
import {
  GST_DOCUMENT_TYPES,
  GST_SOURCE_DOCUMENT_KINDS,
  type GstDataSubmission,
  type GstEngagement,
  type GstEngagementRegistration,
  type GstReturnPart,
  type GstSampleFile,
  type GstSourceAnalysis,
  type GstSourceDocument,
} from 'api/types/gstEngagement.types';
import GstCreateSubmissionDialog from './GstCreateSubmissionDialog';
import GstStagingPreviewModal from './GstStagingPreviewModal';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement | null;
  /** Adding samples and finalising follow the `edit` grant. */
  canEdit?: boolean;
  canApprove?: boolean;
  onClose: () => void;
}

const PARTS: { part: GstReturnPart; label: string }[] = [
  { part: 'B2B', label: 'B2B' },
  { part: 'B2C', label: 'B2C' },
  { part: 'CDN', label: 'Credit / debit notes' },
  { part: 'HSN', label: 'HSN summary' },
  { part: 'DOCS', label: 'Documents issued' },
];

const streamLabel = (v: string) => GST_DOCUMENT_TYPES.find((t) => t.value === v)?.label ?? v;
const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const stem = (name: string) => name.replace(/\.[^.]+$/, '');
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface FileChoice { include: boolean; sourceRef: string; displayName: string; fileKind: string; isMandatory: boolean }
interface PartChoice { primary: string; crossChecks: string[] }

const GstSourceSetupModal: React.FC<Props> = ({ isOpen, engagement, canEdit = false, canApprove = false, onClose }) => {
  const [registrations, setRegistrations] = useState<GstEngagementRegistration[]>([]);
  const [registrationId, setRegistrationId] = useState('');
  const [sampleMonth, setSampleMonth] = useState('');
  const [samples, setSamples] = useState<GstSampleFile[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);

  const [parseFor, setParseFor] = useState<GstSourceDocument | null>(null);
  const [preview, setPreview] = useState<GstDataSubmission | null>(null);

  const [analysis, setAnalysis] = useState<GstSourceAnalysis | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const [fileChoices, setFileChoices] = useState<Record<string, FileChoice>>({});
  const [partChoices, setPartChoices] = useState<Partial<Record<GstReturnPart, PartChoice>>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalising, setFinalising] = useState(false);
  const [openComparison, setOpenComparison] = useState<number | null>(null);

  const loadSamples = useCallback(async () => {
    if (!engagement) return;
    try { setSamples(await gstSourceSetupService.listSamples(engagement.id)); }
    catch (err: any) { notificationService.toast.error(err?.response?.data?.message ?? 'Could not load the samples.'); }
  }, [engagement]);

  useEffect(() => {
    if (!isOpen || !engagement) return;
    setAnalysis(null); setFile(null); setPreview(null);
    gstEngagementService.getById(engagement.id).then((d) => {
      const regs = d.registrations ?? [];
      setRegistrations(regs);
      setRegistrationId((cur) => cur || (regs.length === 1 ? regs[0].registrationId : ''));
    }).catch(() => setRegistrations([]));
    void loadSamples();
  }, [isOpen, engagement, loadSamples]);

  const monthOptions = useMemo(() => {
    if (!engagement) return [];
    const out: { value: string; label: string }[] = [];
    for (let i = 0; i < 12; i++) {
      const s = new Date(engagement.financialYear, 3 + i, 1);
      const e = new Date(engagement.financialYear, 4 + i, 0);
      out.push({ value: `${ymd(s)}|${ymd(e)}`, label: s.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) });
    }
    return out;
  }, [engagement]);

  const regOptions = useMemo(
    () => registrations.map((r) => ({ value: r.registrationId, label: r.tradeName ? `${r.gstin} · ${r.tradeName}` : r.gstin })),
    [registrations],
  );

  // ---- samples --------------------------------------------------------------

  const addSample = async () => {
    if (!engagement || !file) return;
    setBusy(true);
    try {
      const { result } = await gstSourceDocumentService.receive(engagement.id, { file, purpose: 'SAMPLE' });
      const doc = result.receipt.document;
      if (doc.purpose === 'RECEIPT') {
        notificationService.toast.warning(`${doc.fileName} was already received as a real file, so it is not added as a sample.`, 8000);
      } else if (!doc.isMachineReadable) {
        notificationService.toast.warning(`${doc.fileName} is not a spreadsheet (${doc.sniffedFormat}); it cannot be analysed.`, 8000);
      } else {
        if (result.receipt.alreadyReceived) notificationService.toast.info(`${doc.fileName} is already a sample.`);
        if ((doc.feeds?.length ?? 0) === 0) setParseFor(doc);
      }
      setFile(null); setFileKey((k) => k + 1);
      await loadSamples();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not add the sample.');
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async (submissionId: string) => {
    if (!engagement) return;
    try { setPreview(await gstSubmissionService.getById(engagement.id, submissionId)); }
    catch (err: any) { notificationService.toast.error(err?.response?.data?.message ?? 'Could not open the parse.'); }
  };

  const discard = async (doc: GstSourceDocument) => {
    if (!engagement) return;
    try {
      await gstSourceSetupService.discardParses(engagement.id, doc.id);
      notificationService.toast.success(`${doc.fileName}: parse discarded.`);
      setAnalysis(null);
      await loadSamples();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not discard the parse.');
    }
  };

  const [start, end] = sampleMonth ? sampleMonth.split('|') : ['', ''];
  const prefill = registrationId && start
    ? { registrationId, sourceRef: 'MAIN', taxPeriodStart: start, taxPeriodEnd: end }
    : registrationId ? { registrationId, sourceRef: 'MAIN', taxPeriodStart: `${engagement?.financialYear ?? 2026}-04-01`, taxPeriodEnd: `${engagement?.financialYear ?? 2026}-04-30` } : null;

  // ---- analysis -------------------------------------------------------------

  const analyse = async () => {
    if (!engagement || !registrationId) return;
    setAnalysing(true);
    try {
      const a = await gstSourceSetupService.analyse(engagement.id, registrationId);
      setAnalysis(a);
      setOpenComparison(null);

      // Starting point for the decision: every file kept unless it only ever
      // duplicates a primary; names from the file; kinds from what it holds.
      const primaries = new Set(a.recommendations.map((r) => r.primaryDocumentId));
      const fc: Record<string, FileChoice> = {};
      a.files.forEach((f) => {
        const dupEverywhere = !primaries.has(f.documentId)
          && a.recommendations.filter((r) => r.crossCheckDocumentIds.includes(f.documentId)).every((r) => r.duplicateDocumentIds.includes(f.documentId));
        fc[f.documentId] = { include: !dupEverywhere, sourceRef: 'MAIN', displayName: stem(f.fileName), fileKind: f.suggestedKind, isMandatory: true };
      });
      setFileChoices(fc);
      const pc: Partial<Record<GstReturnPart, PartChoice>> = {};
      a.recommendations.forEach((r) => { pc[r.part] = { primary: r.primaryDocumentId, crossChecks: r.crossCheckDocumentIds }; });
      setPartChoices(pc);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not analyse the samples.');
    } finally {
      setAnalysing(false);
    }
  };

  const fileName = (id: string) => analysis?.files.find((f) => f.documentId === id)?.fileName ?? 'file';

  const finalise = async () => {
    if (!engagement || !analysis) return;
    setConfirmOpen(false);
    setFinalising(true);
    try {
      const kept = new Set(Object.entries(fileChoices).filter(([, c]) => c.include).map(([id]) => id));
      const { message, notes } = await gstSourceSetupService.finalise(engagement.id, {
        registrationId,
        files: Object.entries(fileChoices).map(([documentId, c]) => ({ documentId, ...c })),
        parts: (Object.entries(partChoices) as [GstReturnPart, PartChoice][])
          .filter(([, c]) => kept.has(c.primary))
          .map(([part, c]) => ({
            part, primaryDocumentId: c.primary,
            crossCheckDocumentIds: c.crossChecks.filter((x) => kept.has(x) && x !== c.primary),
            evidence: analysis.recommendations.find((r) => r.part === part)?.reason ?? null,
          })),
      });
      notificationService.toast.success(message, 6000);
      if (notes.length) notificationService.toast.info(notes.join(' '), 8000);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not finalise the setup.', 9000);
    } finally {
      setFinalising(false);
    }
  };

  if (!isOpen || !engagement) return null;

  const regSamples = samples.filter((s) => s.parses.length === 0 || s.parses.some((p) => p.registrationId === registrationId));
  const th: React.CSSProperties = { padding: '4px 8px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.75rem' };
  const td: React.CSSProperties = { padding: '5px 8px', borderTop: '1px solid var(--border-color)', verticalAlign: 'top', fontSize: '0.8125rem' };
  const section: React.CSSProperties = { border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.75rem', marginBottom: 12 };
  const linkBtn: React.CSSProperties = { border: 'none', background: 'transparent', padding: 0, color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.75rem' };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl">
        {finalising && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving the setup…</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Source setup — {engagement.clientLegalName || engagement.clientName}</h2>
          <button className="modal-close" onClick={onClose} title="Close"><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 10 }}>
            Upload one month of every file the client sends — register, B2B, B2C, HSN, credit notes, GST reports — together.
            They are read and compared, never counted towards a return. Then decide which to rely on; the first real month
            then parses with no questions.
          </div>

          <div className="form-grid-2" style={{ marginBottom: 6 }}>
            <div className="form-group">
              <SyutSelect label={<>GSTIN <span className="text-danger">*</span></>} options={regOptions} value={registrationId} isMulti={false} isClearable={false} size="sm"
                onChange={(v) => { setRegistrationId((v as string) || ''); setAnalysis(null); }} />
            </div>
            <div className="form-group">
              <SyutSelect label="The samples are for" options={monthOptions} value={sampleMonth} isMulti={false} isClearable={false} size="sm"
                onChange={(v) => setSampleMonth((v as string) || '')} />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>Used to check the invoice dates in the samples.</div>
            </div>
          </div>

          {/* ---- 1. samples ---- */}
          <div style={section}>
            <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 8 }}>1 · Sample files</div>
            {canEdit && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <input key={fileKey} type="file" className="form-input" style={{ maxWidth: 360 }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} disabled={busy || !registrationId} />
                <button className="btn btn-primary" disabled={busy || !file || !registrationId || !sampleMonth} onClick={() => { void addSample(); }}>
                  {busy ? 'Adding…' : 'Add sample'}
                </button>
                {(!registrationId || !sampleMonth) && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Choose the GSTIN and the month first.</span>}
              </div>
            )}
            {regSamples.length === 0 ? (
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>No samples yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>File</th><th style={th}>Read as</th><th style={th}>Mapping</th><th style={th} /></tr></thead>
                <tbody>
                  {regSamples.map((s) => (
                    <tr key={s.document.id}>
                      <td style={td}>{s.document.fileName}<div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{s.document.sniffedFormat}</div></td>
                      <td style={td}>
                        {s.parses.length === 0
                          ? <span style={{ color: '#d97706' }}>not parsed yet</span>
                          : s.parses.map((p) => <div key={p.submissionId}>{streamLabel(p.documentType)} <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>· {p.sheetNames.join(', ')} · {p.dataRows.toLocaleString()} rows</span></div>)}
                      </td>
                      <td style={td}>
                        {s.parses.map((p) => (
                          <div key={p.submissionId}>
                            {p.mappingProfileId ? 'profile recorded' : <span style={{ color: 'var(--text-secondary)' }}>a fitting profile is used if there is one</span>}
                            {' · '}<button type="button" style={linkBtn} onClick={() => { void openPreview(p.submissionId); }}>Open / map</button>
                          </div>
                        ))}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {canEdit && s.parses.length === 0 && (
                          <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '2px 10px' }} disabled={!registrationId || !sampleMonth} onClick={() => setParseFor(s.document)}>Parse</button>
                        )}
                        {canEdit && s.parses.length > 0 && (
                          <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '2px 10px' }} onClick={() => { void discard(s.document); }}>Discard parse</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ---- 2. analysis ---- */}
          <div style={section}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>2 · What the files say</div>
              <span style={{ flex: 1 }} />
              <button className="btn btn-primary" disabled={analysing || !registrationId || regSamples.every((s) => s.parses.length === 0)} onClick={() => { void analyse(); }}>
                {analysing ? 'Analysing…' : analysis ? 'Analyse again' : 'Analyse'}
              </button>
            </div>

            {!analysis && <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Parse the samples, map any that need it, then analyse.</div>}

            {analysis && (
              <>
                {analysis.gaps.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    {analysis.gaps.map((g, i) => (
                      <div key={i} style={{ fontSize: '0.8125rem', color: g.severity === 'ERROR' ? '#dc2626' : g.severity === 'WARNING' ? '#d97706' : 'var(--text-secondary)' }}>
                        {g.severity === 'ERROR' ? '✗' : g.severity === 'WARNING' ? '!' : 'i'} {g.message}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: '0.8125rem', fontWeight: 600, margin: '6px 0' }}>Coverage — which file provides which part</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
                  <thead>
                    <tr><th style={th}>File</th>{PARTS.map((p) => <th key={p.part} style={th}>{p.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {analysis.files.map((f) => (
                      <tr key={f.documentId}>
                        <td style={td}>{f.fileName}<div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{f.profiles.join(', ')}</div></td>
                        {PARTS.map((p) => {
                          const cells = analysis.coverage.filter((c) => c.documentId === f.documentId && c.part === p.part);
                          if (cells.length === 0) return <td key={p.part} style={{ ...td, color: 'var(--text-secondary)' }}>—</td>;
                          return (
                            <td key={p.part} style={td}>
                              {cells.map((c) => (
                                <div key={c.label} title={c.missingFields.map((m) => `${m.fieldName}: missing on ${m.missing} of ${m.of}`).join('\n') || 'Every required field present'}>
                                  <span className={c.level === 'FULL' ? 'badge badge-active' : 'badge badge-pending'}>{c.level === 'FULL' ? 'Full' : 'Partial'}</span>
                                  {c.derived && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}> from lines</span>}
                                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                    {c.invoices > 0 ? `${c.invoices.toLocaleString()} inv · ` : ''}{money(c.taxable)}
                                  </div>
                                  {c.missingFields.length > 0 && (
                                    <div style={{ fontSize: '0.7rem', color: '#d97706' }}>no {c.missingFields.map((m) => m.fieldName).join(', ')}{c.missingFields.some((m) => m.missing < m.of) ? ' (some)' : ''}</div>
                                  )}
                                </div>
                              ))}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {analysis.comparisons.length > 0 && (
                  <>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, margin: '6px 0' }}>Where files overlap</div>
                    {analysis.comparisons.map((c, i) => (
                      <div key={i} style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '6px 8px', marginBottom: 6 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.8125rem' }}>
                          <span className={c.agree ? 'badge badge-active' : 'badge badge-pending'}>{c.agree ? 'Agree' : 'Differ'}</span>
                          <strong>{PARTS.find((p) => p.part === c.part)?.label}</strong>
                          <span>{c.labelA} <span style={{ color: 'var(--text-secondary)' }}>vs</span> {c.labelB}</span>
                          <span style={{ flex: 1 }} />
                          <button type="button" style={linkBtn} onClick={() => setOpenComparison(openComparison === i ? null : i)}>{openComparison === i ? 'Hide' : 'Details'}</button>
                        </div>
                        {c.notes.map((n, j) => <div key={j} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{n}</div>)}
                        {openComparison === i && (
                          <div style={{ marginTop: 6 }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                              <thead><tr style={{ textAlign: 'right' }}><th style={{ ...th, textAlign: 'left' }}>Rate</th><th style={th}>Taxable · {c.labelA}</th><th style={th}>Taxable · {c.labelB}</th><th style={th}>Tax · A</th><th style={th}>Tax · B</th></tr></thead>
                              <tbody>
                                {c.rates.map((r, k) => (
                                  <tr key={k} style={{ textAlign: 'right', color: r.agrees ? undefined : '#dc2626' }}>
                                    <td style={{ padding: '2px 8px', textAlign: 'left' }}>{r.rate === null || r.rate === undefined ? 'no rate' : `${r.rate}%`}</td>
                                    <td style={{ padding: '2px 8px' }}>{money(r.taxableA)}</td><td style={{ padding: '2px 8px' }}>{money(r.taxableB)}</td>
                                    <td style={{ padding: '2px 8px' }}>{money(r.taxA)}</td><td style={{ padding: '2px 8px' }}>{money(r.taxB)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {(c.onlyInAExamples.length > 0 || c.onlyInBExamples.length > 0) && (
                              <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
                                {c.onlyInAExamples.length > 0 && <div>Only in {c.labelA}: {c.onlyInAExamples.join(', ')}{c.onlyInA > c.onlyInAExamples.length ? ' …' : ''}</div>}
                                {c.onlyInBExamples.length > 0 && <div>Only in {c.labelB}: {c.onlyInBExamples.join(', ')}{c.onlyInB > c.onlyInBExamples.length ? ' …' : ''}</div>}
                              </div>
                            )}
                            {c.hsnDiffs.length > 0 && (
                              <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
                                HSN differing: {c.hsnDiffs.map((h) => `${h.hsn} (${money(h.taxableA)} vs ${money(h.taxableB)})`).join('; ')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          {/* ---- 3. decide ---- */}
          {analysis && analysis.files.length > 0 && (
            <div style={section}>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 8 }}>3 · Decide</div>

              <div style={{ fontSize: '0.8125rem', fontWeight: 600, margin: '6px 0' }}>Which files the client will send each month</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
                <thead><tr><th style={th}>Keep</th><th style={th}>Sample</th><th style={th}>Branch</th><th style={th}>Name</th><th style={th}>Kind</th><th style={th}>Mandatory</th></tr></thead>
                <tbody>
                  {analysis.files.map((f) => {
                    const c = fileChoices[f.documentId];
                    if (!c) return null;
                    const set = (patch: Partial<FileChoice>) => setFileChoices((prev) => ({ ...prev, [f.documentId]: { ...prev[f.documentId], ...patch } }));
                    return (
                      <tr key={f.documentId} style={{ opacity: c.include ? 1 : 0.55 }}>
                        <td style={td}><input type="checkbox" checked={c.include} onChange={(e) => set({ include: e.target.checked })} /></td>
                        <td style={td}>{f.fileName}</td>
                        <td style={td}><input className="form-input" style={{ width: 90, fontSize: '0.8125rem', padding: '3px 6px' }} value={c.sourceRef} disabled={!c.include}
                          onChange={(e) => set({ sourceRef: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} /></td>
                        <td style={td}><input className="form-input" style={{ fontSize: '0.8125rem', padding: '3px 6px' }} value={c.displayName} maxLength={200} disabled={!c.include}
                          onChange={(e) => set({ displayName: e.target.value })} /></td>
                        <td style={td}>
                          <select className="form-select" style={{ fontSize: '0.8125rem', padding: '3px 6px' }} value={c.fileKind} disabled={!c.include} onChange={(e) => set({ fileKind: e.target.value })}>
                            {GST_SOURCE_DOCUMENT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                          </select>
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={c.isMandatory} disabled={!c.include} onChange={(e) => set({ isMandatory: e.target.checked })} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ fontSize: '0.8125rem', fontWeight: 600, margin: '6px 0' }}>Which file each part of the return is built from</div>
              {analysis.recommendations.map((r) => {
                const c = partChoices[r.part];
                if (!c) return null;
                const providers = Array.from(new Set(analysis.coverage.filter((x) => x.part === r.part).map((x) => x.documentId)));
                return (
                  <div key={r.part} style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '6px 8px', marginBottom: 6 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.8125rem' }}>
                      <strong style={{ minWidth: 150 }}>{PARTS.find((p) => p.part === r.part)?.label}</strong>
                      <span style={{ color: 'var(--text-secondary)' }}>built from</span>
                      <select className="form-select" style={{ fontSize: '0.8125rem', padding: '3px 6px', maxWidth: 320 }} value={c.primary}
                        onChange={(e) => setPartChoices((prev) => ({ ...prev, [r.part]: { primary: e.target.value, crossChecks: providers.filter((p) => p !== e.target.value) } }))}>
                        {providers.map((p) => <option key={p} value={p}>{fileName(p)}</option>)}
                      </select>
                      {r.needsDecision && <span className="badge badge-pending">Files disagree — your call</span>}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>{r.reason}</div>
                    {providers.filter((p) => p !== c.primary).length > 0 && (
                      <div style={{ fontSize: '0.75rem', marginTop: 2 }}>
                        Cross-checked against:{' '}
                        {providers.filter((p) => p !== c.primary).map((p) => (
                          <label key={p} style={{ marginRight: 10 }}>
                            <input type="checkbox" checked={c.crossChecks.includes(p)}
                              onChange={(e) => setPartChoices((prev) => ({
                                ...prev,
                                [r.part]: { ...c, crossChecks: e.target.checked ? [...c.crossChecks, p] : c.crossChecks.filter((x) => x !== p) },
                              }))} />{' '}
                            {fileName(p)}{r.duplicateDocumentIds.includes(p) ? ' (same data)' : ''}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {canEdit && analysis && (
            <button className="btn btn-primary" disabled={finalising || Object.values(fileChoices).every((c) => !c.include)} onClick={() => setConfirmOpen(true)}>
              Finalise setup
            </button>
          )}
        </div>
      </div>

      <GstCreateSubmissionDialog
        isOpen={parseFor !== null}
        engagement={engagement}
        document={parseFor}
        prefill={prefill}
        sample
        onClose={() => setParseFor(null)}
        onCreated={(subs) => { setParseFor(null); setAnalysis(null); void loadSamples(); if (subs[0]) setPreview(subs[0]); }}
      />

      <GstStagingPreviewModal
        isOpen={preview !== null}
        engagement={engagement}
        submission={preview}
        canSelectProfile={canEdit}
        canApproveProfile={canApprove}
        onClose={() => { setPreview(null); void loadSamples(); }}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Finalise the setup?"
        message={`This declares ${Object.values(fileChoices).filter((c) => c.include).length} expected file(s) for this GSTIN, with the sheets and mapping profiles learned from the samples, and records which file each part of the return is built from. Files already declared under the same branch and name are kept. You can run it again.`}
        confirmLabel="Finalise"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void finalise(); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default GstSourceSetupModal;
